/**
 * Discovery Dashboard page
 * Overview of connections, scans, and classification metrics
 */

import { useNavigate } from 'react-router-dom'
import {
  RefreshCw,
  AlertCircle,
  Moon,
  Sun,
  Plus,
  Link2,
  ScanSearch,
  Columns3,
  ShieldAlert,
} from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { MetricCard } from '@/components/dashboard'
import { ScanStatusBadge } from '@/components/discovery'
import { useConnections, useScans } from '@/hooks/use-discovery'
import { useThemeStore } from '@/stores/theme-store'
import { formatRelativeTime } from '@/lib/discovery-utils'
import { cn } from '@/lib/utils'

export function DiscoveryDashboardPage() {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useThemeStore()

  const {
    data: connectionsData,
    isLoading: connectionsLoading,
    isError: connectionsError,
    error: connError,
    refetch: refetchConnections,
  } = useConnections({ pageSize: 100 })

  const {
    data: scansData,
    isLoading: scansLoading,
    isError: scansError,
    error: scanErr,
    refetch: refetchScans,
  } = useScans({ pageSize: 10 })

  const isLoading = connectionsLoading || scansLoading
  const isError = connectionsError || scansError
  const error = connError ?? scanErr

  const connections = connectionsData?.data ?? []
  const scans = scansData?.data ?? []
  const recentScans = scans.slice(0, 5)

  const totalConnections = connectionsData?.pagination?.totalItems ?? 0
  const activeScans = scans.filter(
    (s) => s.status === 'RUNNING' || s.status === 'PENDING'
  ).length
  const totalColumnsScanned = scans.reduce(
    (sum, s) => sum + s.columnsClassified,
    0
  )
  const flaggedColumns = 0

  const handleRefresh = () => {
    refetchConnections()
    refetchScans()
  }

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Data Discovery</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isLoading}
            className="h-8"
          >
            <RefreshCw
              className={cn('mr-2 h-3.5 w-3.5', isLoading && 'animate-spin')}
            />
            Refresh
          </Button>
          <Button size="sm" className="h-8" onClick={() => navigate('/discovery/connections/new')}>
            <Plus className="mr-2 h-3.5 w-3.5" />
            New Connection
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        <div>
          <p className="text-sm text-muted-foreground">
            Connect to databases and classify sensitive data automatically
          </p>
        </div>

        {/* Error State */}
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                Failed to load data:{' '}
                {error instanceof Error ? error.message : 'Unknown error'}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="ml-4"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Metric Cards */}
        <div className="*:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card grid grid-cols-1 gap-4 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:shadow-xs sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Connections"
            value={totalConnections}
            description="Database connections configured"
            icon={Link2}
            isLoading={isLoading}
          />
          <MetricCard
            title="Active Scans"
            value={activeScans}
            description="Currently running scans"
            icon={ScanSearch}
            isLoading={isLoading}
          />
          <MetricCard
            title="Columns Scanned"
            value={totalColumnsScanned}
            description="Across all completed scans"
            icon={Columns3}
            isLoading={isLoading}
          />
          <MetricCard
            title="Flagged"
            value={flaggedColumns}
            description="Columns requiring attention"
            icon={ShieldAlert}
            isLoading={isLoading}
          />
        </div>

        {/* Recent Scans */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Scans</CardTitle>
                <CardDescription>Latest discovery scan activity</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/discovery/scans')}
              >
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : recentScans.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No scans yet. Connect to a database and start scanning.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate('/discovery/connections')}
                >
                  Manage Connections
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Connection</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Columns</TableHead>
                    <TableHead className="text-right">Flagged</TableHead>
                    <TableHead className="text-right">Started</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentScans.map((scan) => {
                    const conn = connections.find(
                      (c) => c.id === scan.connectionId
                    )
                    return (
                      <TableRow
                        key={scan.id}
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(`/discovery/scans/${scan.id}/results`)
                        }
                      >
                        <TableCell className="font-medium">
                          {conn?.name ?? scan.connectionId.slice(0, 8)}
                        </TableCell>
                        <TableCell>
                          <ScanStatusBadge status={scan.status} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {scan.columnsClassified}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {0}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {formatRelativeTime(scan.startedAt ?? scan.createdAt)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
